import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, loginMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("jbesson13800@gmail.com");
  const [password, setPassword] = useState("Fabriko2026!");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");

  const finish = (res) => {
    if (res?.mfa_required) {
      setChallenge(res.challenge_token);
      setCode("");
    } else {
      navigate("/");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      finish(await login(email, password));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginMfa(challenge, code.trim());
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (em) => {
    setError("");
    setEmail(em);
    setPassword("Fabriko2026!");
    setLoading(true);
    try {
      finish(await login(em, "Fabriko2026!"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const TEST_ACCOUNTS = [
    { id: "proprietaire", label: "Propriétaire", email: "jbesson13800@gmail.com", desc: "Atelier complet" },
    { id: "collaborateur", label: "Collaborateur", email: "collaborateur@fabriko.app", desc: "Même atelier" },
    { id: "demo", label: "Démo", email: "demo@fabriko.app", desc: "Bac à sable" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy">
      <div className="hidden lg:flex flex-col justify-center p-14 bg-navy-deep relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-400/10 blur-3xl" />
        <div className="relative z-10">
          <img src="/fabriko-wordmark.webp" alt="Fabriko" className="h-20 xl:h-28 w-auto mb-10" data-testid="login-logo" />
          <h1 className="text-4xl font-display font-bold text-white leading-tight">
            Le logiciel des selliers-garnisseurs
          </h1>
          <p className="mt-5 text-slate-300 text-lg max-w-md">
            Devis, factures, calcul de matière & de temps, plan d'imposition et assistant IA — tout votre atelier au même endroit.
          </p>
          <div className="mt-8 flex gap-6 text-slate-400 text-sm font-mono">
            <span>Devis / Factures</span>
            <span>Calcul matière</span>
            <span>Imposition</span>
          </div>
        </div>
        <p className="absolute bottom-8 left-14 right-14 z-10 text-slate-500 text-xs">
          Développé par Besson Solutions — Solution éditée par LE RÉSEAU STS
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-14 bg-slate-50">
        {challenge ? (
          <form onSubmit={submitMfa} className="w-full max-w-md" data-testid="mfa-form">
            <div className="w-12 h-12 rounded-2xl bg-navy flex items-center justify-center mb-5">
              <ShieldCheck className="w-6 h-6 text-sky-400" />
            </div>
            <h2 className="text-3xl font-display font-bold text-navy">Vérification en deux étapes</h2>
            <p className="text-slate-500 mt-2">Saisissez le code à 6 chiffres de votre application d'authentification, ou un code de secours.</p>

            {error && (
              <div className="mt-6 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="mfa-error">
                {error}
              </div>
            )}

            <label className="block mt-6 text-sm font-medium text-slate-700">Code de vérification</label>
            <input
              autoFocus
              inputMode="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="mfa-code-input"
              placeholder="123456"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white font-mono tracking-widest text-lg"
              required
            />

            <button type="submit" disabled={loading} data-testid="mfa-submit-button"
              className="mt-6 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Vérifier
            </button>
            <button type="button" onClick={() => { setChallenge(""); setError(""); }} data-testid="mfa-back-button"
              className="mt-4 w-full text-sm text-slate-500 hover:text-navy inline-flex items-center justify-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Revenir à la connexion
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/fabriko-icon.png" alt="Fabriko" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-display font-bold text-navy">Fabriko</span>
            </div>
            <h2 className="text-3xl font-display font-bold text-navy">Connexion</h2>
            <p className="text-slate-500 mt-2">Accédez à votre atelier</p>

            {error && (
              <div className="mt-6 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <label className="block mt-6 text-sm font-medium text-slate-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
              required
            />

            <label className="block mt-4 text-sm font-medium text-slate-700">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
              required
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="mt-6 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Se connecter
            </button>

            <p className="mt-6 text-center text-sm text-slate-500">
              Pas encore de compte ?{" "}
              <Link to="/register" className="text-sky-600 font-semibold hover:underline" data-testid="go-register-link">
                Créer un atelier
              </Link>
            </p>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider text-center mb-3">
                Connexion rapide — comptes de test
              </p>
              <div className="grid grid-cols-3 gap-2">
                {TEST_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    onClick={() => quickLogin(a.email)}
                    disabled={loading}
                    data-testid={`quick-login-${a.id}`}
                    className="flex flex-col items-center gap-0.5 px-2 py-3 rounded-xl border border-slate-200 bg-white hover:border-sky-400 hover:bg-sky-50/40 transition-colors disabled:opacity-60"
                  >
                    <span className="text-sm font-semibold text-navy">{a.label}</span>
                    <span className="text-[10px] text-slate-400 text-center leading-tight">{a.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
